/*
 * Cancha landing sections: video modal playback, review carousel progress and the sticky shop bar.
 * Carousels, arrows, tabs and modals themselves are the theme's own components (theme.js).
 */
(() => {
  if (window.CanchaLanding) return;
  window.CanchaLanding = true;

  // The theme's <x-modal> opens on any [aria-controls] click. The player is kept in a <template> so the video is
  // only fetched once someone opens the popup. Play it once visible and stop it on close.
  // Dialog events do not bubble, so listen in the capture phase.
  document.addEventListener('dialog:before-show', (event) => {
    if (!event.target.matches?.('.cl-video-modal')) return;
    const modal = event.target;
    const template = modal.querySelector('template.cl-video-modal__player');
    if (!template) return;
    modal.clTemplate = template;
    template.replaceWith(template.content.cloneNode(true));

    // The theme retries a play() that fails while the video is still loading. If the popup was closed in the
    // meantime, stop that late start so the video never plays behind a closed popup.
    modal.querySelector('video')?.addEventListener('play', (playEvent) => {
      if (!modal.hasAttribute('open')) playEvent.target.pause();
    });
  }, true);

  document.addEventListener('dialog:after-show', (event) => {
    if (!event.target.matches?.('.cl-video-modal')) return;
    // video-media.play() skips elements whose offsetParent is null, which is always the case inside the modal's
    // shadow layout, so drive the player directly.
    const media = event.target.querySelector('video-media');
    if (media && !media.playing) media.player.play();
  }, true);

  document.addEventListener('dialog:after-hide', (event) => {
    if (!event.target.matches?.('.cl-video-modal')) return;
    const modal = event.target;
    modal.querySelector('video-media')?.pause();
    modal.querySelector('video')?.pause();
    // A YouTube / Vimeo frame can't be paused from here, so put the template back: that stops it, and the
    // next open loads it again from the start.
    const iframe = modal.querySelector('.cl-video-modal__frame > iframe');
    if (iframe && modal.clTemplate) iframe.replaceWith(modal.clTemplate);
  }, true);

  // Theme editor: selecting a tab block shows that tab.
  document.addEventListener('shopify:block:select', (event) => {
    if (event.target.matches?.('.cl-tabs__tab')) event.target.click();
  });

  // Thin progress bar under a theme <scroll-carousel>, matching the lifestyle carousel.
  class CanchaCarouselProgress extends HTMLElement {
    connectedCallback() {
      this.carousel = document.getElementById(this.dataset.carousel);
      this.thumb = this.querySelector('span');
      if (!this.carousel || !this.thumb) return;
      this.update = this.update.bind(this);
      this.carousel.addEventListener('scroll', this.update, { passive: true });
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.carousel);
      this.update();
    }

    disconnectedCallback() {
      this.carousel?.removeEventListener('scroll', this.update);
      this.observer?.disconnect();
    }

    update() {
      const { scrollWidth, clientWidth } = this.carousel;
      const scrollLeft = Math.abs(this.carousel.scrollLeft);
      const visible = scrollWidth > 0 ? Math.min(1, clientWidth / scrollWidth) : 1;
      const travel = scrollWidth - clientWidth;
      const progress = travel > 0 ? scrollLeft / travel : 0;
      this.hidden = visible >= 1;
      this.thumb.style.width = `${visible * 100}%`;
      // The thumb starts at the inline start, so it moves left on right-to-left pages.
      const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.thumb.style.transform = `translateX(${direction * progress * (1 / visible - 1) * 100}%)`;
    }
  }

  if (!customElements.get('cancha-carousel-progress')) {
    customElements.define('cancha-carousel-progress', CanchaCarouselProgress);
  }

  // Hero background video. The hero image is what loads first (it is the page's main image), so the video is
  // only requested once the page has finished loading, then fades in over the image when it can play.
  // Visitors who ask for reduced motion or data saving keep the still image. Pauses while scrolled away.
  class CanchaHeroVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('.cl-hero__video-toggle');
      if (!this.video) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData) return;

      // Every listener is tied to this signal and removed on disconnect, so moving the section
      // (e.g. in the theme editor) doesn't stack duplicate handlers.
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.userPaused = false;
      this.toggle?.addEventListener('click', () => {
        this.userPaused = !this.video.paused;
        if (this.userPaused) this.video.pause(); else this.play();
      }, { signal });
      this.video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true, signal });
      this.video.addEventListener('play', () => this.updateToggle(), { signal });
      this.video.addEventListener('pause', () => this.updateToggle(), { signal });

      if (document.readyState === 'complete') {
        this.start();
      } else {
        window.addEventListener('load', () => this.start(), { once: true, signal });
      }
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.observer?.disconnect();
    }

    start() {
      const mobile = window.matchMedia('(max-width: 699px)').matches;
      const src = (mobile && this.dataset.srcMobile) || this.dataset.src;
      if (!src || !this.isConnected) return;
      this.video.src = src;
      this.video.preload = 'auto';
      this.updateToggle();
      if (this.toggle) this.toggle.hidden = false;

      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !this.userPaused) this.play(); else this.video.pause();
      });
      this.observer.observe(this);
    }

    play() {
      // A blocked autoplay rejects without a pause event, so refresh the button either way.
      this.video.play()?.catch(() => this.updateToggle());
    }

    updateToggle() {
      if (!this.toggle) return;
      const paused = this.video.paused;
      this.classList.toggle('is-paused', paused);
      this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
    }
  }

  if (!customElements.get('cancha-hero-video')) {
    customElements.define('cancha-hero-video', CanchaHeroVideo);
  }

  // Sticky shop bar: appears once the landing hero has scrolled away, hides again over the footer
  // and over the section placed just before it (the closing call to action), when that option is on.
  class CanchaStickyBar extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.cl-sticky');
      const hero = document.querySelector('.cl-hero') || document.querySelector('main .shopify-section');
      const stops = [
        ...document.querySelectorAll('[data-cl-sticky-stop], .shopify-section-group-footer-group')
      ];
      const previousSection = this.closest('.shopify-section')?.previousElementSibling;
      if (this.hasAttribute('hide-over-previous') && previousSection) stops.push(previousSection);
      if (!this.bar || !hero) return;

      this.blocking = new Set();
      this.heroVisible = true;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === hero) {
            this.heroVisible = entry.isIntersecting;
          } else if (entry.isIntersecting) {
            this.blocking.add(entry.target);
          } else {
            this.blocking.delete(entry.target);
          }
        });
        this.render();
      });

      this.observer.observe(hero);
      stops.forEach((element) => this.observer.observe(element));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    render() {
      const visible = !this.heroVisible && this.blocking.size === 0;
      this.bar.classList.toggle('is-visible', visible);
      this.bar.toggleAttribute('inert', !visible);
    }
  }

  if (!customElements.get('cancha-sticky-bar')) {
    customElements.define('cancha-sticky-bar', CanchaStickyBar);
  }
})();
